import React from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';

const CHUNK_ERROR_PATTERN = /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i;

const getErrorMessage = (error) => {
  if (isRouteErrorResponse(error)) {
    return error.statusText || error.data?.message || `Request failed with ${error.status}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error || 'Unknown application error.');
};

const isChunkLoadError = (error) => {
  const message = getErrorMessage(error);
  const name = error instanceof Error ? error.name : '';
  return CHUNK_ERROR_PATTERN.test(`${name} ${message}`);
};

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const message = getErrorMessage(error);
  const chunkError = isChunkLoadError(error);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <main className="min-h-screen bg-[var(--color-bg-base)] px-4 py-8 text-[var(--color-text-base)] sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl items-center justify-center">
        <section className="w-full rounded-lg border border-[var(--color-border-main)] bg-[var(--color-bg-card)] p-5 shadow-sm sm:p-7">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[var(--color-accent-primary)]/12 text-[var(--color-accent-primary)]">
              <AlertTriangle size={24} />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--color-accent-primary)]">
                {chunkError ? 'App update interrupted' : 'Application error'}
              </p>
              <h1 className="mt-2 break-words text-2xl font-black leading-tight sm:text-3xl">
                {chunkError ? 'This page needs a fresh reload.' : 'Something went wrong.'}
              </h1>
              <p className="mt-3 text-sm leading-6 text-[var(--color-text-muted)]">
                {chunkError
                  ? 'The app could not load one of its JavaScript files. This can happen after a rebuild, deploy, slow localhost response, or stale browser cache.'
                  : 'The app hit an unexpected route error. You can reload the app or return to the dashboard.'}
              </p>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={handleReload}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-text-on-accent)] transition hover:opacity-90"
                >
                  <RefreshCw size={16} />
                  Reload app
                </button>
                <Link
                  to="/dashboard"
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[var(--color-border-main)] bg-[var(--color-bg-base)] px-4 py-2 text-sm font-semibold text-[var(--color-text-base)] transition hover:bg-[var(--color-bg-hover)]"
                >
                  <Home size={16} />
                  Go to dashboard
                </Link>
              </div>

              <details className="mt-5 rounded-lg border border-[var(--color-border-main)] bg-[var(--color-bg-base)] px-4 py-3">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
                  Error details
                </summary>
                <pre className="mt-3 max-h-52 overflow-auto whitespace-pre-wrap break-words text-xs leading-5 text-[var(--color-text-muted)]">
                  {message}
                </pre>
              </details>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default RouteErrorBoundary;
